import React from 'react';
import { motion } from 'framer-motion';
import APIService from '../../services/api/index.api';
import MoneyAmount from '../MoneyAmount';
import './index.scss';
import { StatsType } from '../../services/api/types/stats.response.d';
import { useSelector } from 'react-redux';
import { getFormDemoSubmission } from '../../store/selectors';
import Button from '../Button';
import WatermarkPill from '../WatermarkPill';

export interface SubmitScreenProps {
  leadId: number;
  logoImageId?: number | null;
  title: string;
  description: string;
  price: number | null;
  clientKey: string;
  phoneNumber: string;
  phoneCtaText: string | null;
  startingFromText: string;
  resultScreenButtonRedirect?: string | null;
  startingFromDisclaimer?: string | null;
  displayButton?: boolean;
}

function SubmitScreen({
  leadId,
  logoImageId,
  title,
  description,
  price,
  clientKey,
  phoneNumber,
  phoneCtaText,
  startingFromText,
  resultScreenButtonRedirect,
  startingFromDisclaimer,
  displayButton = true,
}: SubmitScreenProps) {
  const isDemoSubmission = useSelector(getFormDemoSubmission);
  const phoneCtaCallback = React.useCallback(() => {
    if (!isDemoSubmission) {
      APIService.statsService.report({
        type: StatsType.START_PHONE_CALL_SUBMIT,
        leadId,
        clientKey,
      });
    }
    if (resultScreenButtonRedirect?.length) {
      window.open(resultScreenButtonRedirect, '_blank');
    } else {
      window.open(`tel:${phoneNumber}`, '_parent');
    }
  }, [phoneNumber, leadId, clientKey, resultScreenButtonRedirect]);

  const BASE_DELAY = 0.2;

  return (
    <div className="submit-screen-component-container">
      <div className="submit-screen-component-container-top">
        {logoImageId ? (
          <motion.img
            className="submit-screen-component-container-top-image"
            src={APIService.imagesService.createImageUrl(logoImageId)}
            initial={{ opacity: 0, translateY: '15%' }}
            animate={{ opacity: 1, translateY: '0%' }}
            transition={{
              duration: 0.7,
              delay: BASE_DELAY + 0,
              ease: 'anticipate',
            }}
          />
        ) : null}
        <div
          className={`submit-screen-component-container-top-right ${
            !logoImageId ? 'no-image' : ''
          }`}
        >
          <motion.h1
            className={`submit-screen-component-container-top-right-title ${
              !logoImageId ? 'no-image' : ''
            }`}
            initial={{ opacity: 0, translateY: '15%' }}
            animate={{ opacity: 1, translateY: '0%' }}
            transition={{
              duration: 0.7,
              delay: BASE_DELAY + 0.25,
              ease: 'anticipate',
            }}
          >
            {title}
          </motion.h1>
          <motion.p
            className={`submit-screen-component-container-top-right-description ${
              !logoImageId ? 'no-image' : ''
            }`}
            initial={{ opacity: 0, translateY: '15%' }}
            animate={{ opacity: 1, translateY: '0%' }}
            transition={{
              duration: 0.7,
              delay: BASE_DELAY + 0.35,
              ease: 'anticipate',
            }}
          >
            {description}
          </motion.p>
          {price !== null ? (
            <motion.div
              className={`submit-screen-component-container-top-right-price ${
                !logoImageId ? 'no-image' : ''
              }`}
              initial={{ opacity: 0, translateY: '15%' }}
              animate={{
                opacity: 1,
                translateY: '0%',
                transition: {
                  duration: 0.7,
                  delay: BASE_DELAY + 0.4,
                  ease: 'anticipate',
                },
              }}
            >
              <span className="submit-screen-component-container-top-right-price-label">
                {startingFromText}
              </span>
              <MoneyAmount
                className="submit-screen-component-container-top-right-price-amount"
                cents={price}
              />
              {startingFromDisclaimer?.length ? (
                <span className="submit-screen-component-container-top-right-price-disclaimer">
                  {startingFromDisclaimer}
                </span>
              ) : null}
            </motion.div>
          ) : null}
          {(phoneNumber?.length || resultScreenButtonRedirect) &&
          displayButton ? (
            <>
              {phoneCtaText?.length ? (
                <motion.div
                  className={`submit-screen-component-container-top-right-quote-holder ${
                    !logoImageId ? 'no-image' : ''
                  }`}
                  initial={{ opacity: 0, translateY: '15%' }}
                  animate={{
                    opacity: 1,
                    translateY: '0%',
                    transition: {
                      duration: 0.7,
                      delay: BASE_DELAY + 0.5,
                      ease: 'anticipate',
                    },
                  }}
                >
                  <Button
                    className={`submit-screen-component-container-top-right-quote ${
                      !logoImageId ? 'no-image' : ''
                    }`}
                    onClick={phoneCtaCallback}
                    text={phoneCtaText}
                    disabled={false}
                  />
                </motion.div>
              ) : null}
              <motion.div
                className="submit-screen-component-container-top-right-notice"
                initial={{ opacity: 0, translateY: '15%' }}
                animate={{
                  opacity: 1,
                  translateY: '0%',
                  transition: {
                    duration: 0.7,
                    delay: BASE_DELAY + 0.6,
                    ease: 'anticipate',
                  },
                }}
              >
                <WatermarkPill />
              </motion.div>
            </>
          ) : (
            <>
              <motion.div
                className="submit-screen-component-container-top-right-notice"
                initial={{ opacity: 0, translateY: '15%' }}
                animate={{
                  opacity: 1,
                  translateY: '0%',
                  transition: {
                    duration: 0.7,
                    delay: BASE_DELAY + 0.6,
                    ease: 'anticipate',
                  },
                }}
              >
                <WatermarkPill />
              </motion.div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubmitScreen;
